/**
 * FieldSelector — large modal picker for table columns / summary fields,
 * matching trading-bot-app's "Position Columns" / "Configure Account Info"
 * dialogs: quick-preset chips (optional), Show All / Hide All, a grid of
 * category cards with per-group counts, and a Reset + Done footer.
 *
 * SSoT for "checkbox list of things the user wants visible" — used by the
 * trading positions table and the sidebar account summary.
 */
import * as React from 'react'
import { Settings, RotateCcw, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { Checkbox } from './checkbox'
import {
  Dialog, DialogClose, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogTrigger,
} from './dialog'

export interface FieldDefinition {
  id: string
  label: string
  category: string
  visible: boolean
}

export interface FieldPreset {
  key: string
  label: string
  fields: string[]
}

interface FieldSelectorProps {
  fields: FieldDefinition[]
  onFieldsChange: (next: FieldDefinition[]) => void
  defaultVisible?: string[]
  categoryLabels?: Record<string, string>
  title?: string
  description?: string
  trigger?: React.ReactNode
  /** Optional one-click column sets, shown as chips above the grid. */
  presets?: FieldPreset[]
}

export function FieldSelector({
  fields, onFieldsChange, defaultVisible,
  categoryLabels = {}, title = 'Configure fields',
  description, trigger, presets,
}: FieldSelectorProps) {
  const groups = React.useMemo(() => {
    const m: Record<string, FieldDefinition[]> = {}
    for (const f of fields) {
      m[f.category] ??= []
      m[f.category].push(f)
    }
    return m
  }, [fields])

  const toggle = (id: string) => {
    onFieldsChange(fields.map((f) => (f.id === id ? { ...f, visible: !f.visible } : f)))
  }

  const setAll = (visible: boolean) => {
    onFieldsChange(fields.map((f) => ({ ...f, visible })))
  }

  const reset = () => {
    if (!defaultVisible) return
    onFieldsChange(fields.map((f) => ({ ...f, visible: defaultVisible.includes(f.id) })))
  }

  const applyPreset = (preset: FieldPreset) => {
    const set = new Set(preset.fields)
    onFieldsChange(fields.map((f) => ({ ...f, visible: set.has(f.id) })))
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <button className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
            <Settings className="h-3.5 w-3.5" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[920px] max-h-[88vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-4">
          {presets && presets.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                Quick Presets
              </div>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <Button
                    key={p.key}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => applyPreset(p)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAll(true)}>
              <Eye className="h-3.5 w-3.5 mr-1.5" /> Show All
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAll(false)}>
              <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Hide All
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(groups).map(([cat, items]) => {
              const visibleCount = items.filter((f) => f.visible).length
              return (
                <div key={cat} className="rounded-lg border border-border bg-card/40 p-4">
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-border/60">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                      {categoryLabels[cat] ?? cat}
                    </div>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                      {visibleCount}/{items.length}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {items.map((f) => (
                      <label
                        key={f.id}
                        className="flex items-center gap-2.5 py-1.5 px-1.5 rounded hover:bg-accent/50 cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={f.visible}
                          onCheckedChange={() => toggle(f.id)}
                        />
                        <span className={cn(!f.visible && 'text-muted-foreground')}>
                          {f.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          {defaultVisible ? (
            <Button variant="outline" size="sm" onClick={reset}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
            </Button>
          ) : <span />}
          <DialogClose asChild>
            <Button size="sm">Done</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}
